<template>
  <div>
    <!-- Chart or empty state -->
    <div v-if="isFetching && !profile" class="tw-flex tw-gap-2 tw-items-center tw-justify-center tw-px-4 tw-py-8 tw-text-sm tw-text-gray-400">
      <span class="tw-w-4 tw-h-4 tw-border-2 tw-border-gray-300 tw-border-t-blue-400 tw-rounded-full tw-animate-spin" />
      Laden...
    </div>

    <div v-else-if="!profile || profile.length === 0" class="tw-px-4 tw-py-6 tw-text-sm tw-text-gray-400 tw-text-center">
      Geen spanningsdata beschikbaar voor {{ formattedDate }}.
    </div>

    <div v-else style="height: 280px;" class="tw-px-2 tw-pb-2">
      <ChartLine :data="chartData" :options="chartOptions" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useQuery, useQueries } from '@tanstack/vue-query'
import fetchData from '@/util/fetch'
import { format, parseISO, addDays } from 'date-fns'
import { Line as ChartLine } from 'vue-chartjs'
import {
  Chart as ChartJS,
  Tooltip,
  Legend,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
} from 'chart.js'

import { NEIGHBOR_COLORS, MAX_NEIGHBORS, VOLTAGE_THRESHOLDS } from './constants'
import { voltageThresholdPlugin } from './chartPlugins'

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Tooltip, Legend, voltageThresholdPlugin)

const props = defineProps({
  serialNumber: { type: String, required: true },
  selectedDate: { type: Date, required: true },
  neighborMeters: { type: Array, default: () => [] },
})

const emit = defineEmits(['update:congestionIntervals'])

const formattedDate = computed(() => format(props.selectedDate, 'dd-MM-yyyy'))

const fetchVoltageData = async (serial) => {
  const dateFrom = format(new Date(props.selectedDate), 'yyyy-MM-dd')
  const dateUntil = format(new Date(addDays(props.selectedDate, 1)), 'yyyy-MM-dd')
  const urlParams = new URLSearchParams({ date_from: dateFrom, date_until: dateUntil })
  const result = await fetchData(`/eleena/api/v1/meters/${serial}/power-logs?${urlParams}`)
  return result?.status === 'OK' ? result.data : null
}

// Main meter query
const { data: voltageData, isFetching } = useQuery({
  queryKey: computed(() => ['voltageData', props.serialNumber, formattedDate.value]),
  queryFn: () => fetchVoltageData(props.serialNumber),
  refetchOnWindowFocus: false,
  keepPreviousData: true,
})

// Neighbor voltage queries via useQueries
const neighborQueries = computed(() =>
  props.neighborMeters.slice(0, MAX_NEIGHBORS).map(meter => ({
    queryKey: ['voltageData', meter.serialNumber, formattedDate.value],
    queryFn: () => fetchVoltageData(meter.serialNumber),
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  }))
)

const neighborResults = useQueries({ queries: neighborQueries })

// Process neighbor data reactively
const neighborDatasets = ref([])

watch(
  neighborResults,
  (results) => {
    neighborDatasets.value = results
      .map((query, idx) => {
        const neighborProfile = query?.data?.power_quality_profile
        if (!neighborProfile || neighborProfile.length === 0) return null
        const meter = props.neighborMeters[idx]
        return {
          label: meter?.address || meter?.serialNumber || `Buur ${idx + 1}`,
          data: neighborProfile.map(item => item.avg_voltage_l1),
          borderColor: NEIGHBOR_COLORS[idx % NEIGHBOR_COLORS.length],
          backgroundColor: 'transparent',
          borderWidth: 1.5,
          pointRadius: 0,
          fill: false,
          tension: 0.3,
        }
      })
      .filter(Boolean)
  },
  { deep: true, immediate: true }
)

const profile = computed(() => voltageData?.value?.power_quality_profile)

const datesArray = computed(() =>
  profile.value?.map((item) => {
    const parsedDate = parseISO(item.date)
    return `${String(parsedDate.getUTCHours()).padStart(2, '0')}:${String(
      parsedDate.getUTCMinutes()
    ).padStart(2, '0')}`
  })
)

const mainDataValues = computed(() => profile.value?.map((item) => item.avg_voltage_l1))

// Per-sample point styling so congestion samples stand out on the line itself.
const pointRadius = computed(() =>
  (mainDataValues.value ?? []).map(v => {
    if (v == null) return 0
    if (v > VOLTAGE_THRESHOLDS.CRITICAL) return 3
    if (v > VOLTAGE_THRESHOLDS.WARNING) return 2
    return 0
  })
)

const pointBackgroundColor = computed(() =>
  (mainDataValues.value ?? []).map(v => {
    if (v == null) return 'transparent'
    if (v > VOLTAGE_THRESHOLDS.CRITICAL) return 'rgba(239, 68, 68, 1)'
    if (v > VOLTAGE_THRESHOLDS.WARNING) return 'rgba(245, 158, 11, 1)'
    return 'transparent'
  })
)

// Consecutive samples above WARNING grouped into congestion windows, keyed by
// the same HH:mm label convention that BarChart uses, so the day-view bar
// chart can shade the matching x-range.
const congestionIntervals = computed(() => {
  if (!profile.value?.length) return []
  const intervals = []
  let current = null
  for (const item of profile.value) {
    const v = item.avg_voltage_l1
    const label = format(parseISO(item.date), 'HH:mm')
    if (v != null && v > VOLTAGE_THRESHOLDS.WARNING) {
      const sev = v > VOLTAGE_THRESHOLDS.CRITICAL ? 'critical' : 'warning'
      if (!current) {
        current = { fromLabel: label, toLabel: label, severity: sev, peak: v }
      } else {
        current.toLabel = label
        if (v > current.peak) current.peak = v
        if (sev === 'critical') current.severity = 'critical'
      }
    } else if (current) {
      intervals.push(current)
      current = null
    }
  }
  if (current) intervals.push(current)
  return intervals
})

watch(congestionIntervals, (value) => {
  emit('update:congestionIntervals', value)
}, { immediate: true })

const maxVoltage = computed(() => {
  const values = mainDataValues.value ?? []
  let max = -Infinity
  for (const v of values) if (v != null && v > max) max = v
  return max === -Infinity ? null : max
})

const yMaxSuggested = computed(() =>
  maxVoltage.value != null && maxVoltage.value > VOLTAGE_THRESHOLDS.WARNING ? 256 : 245
)

const chartData = computed(() => ({
  labels: datesArray.value,
  datasets: [
    {
      label: 'Spanning (V)',
      backgroundColor: 'rgba(75, 192, 192, 0.1)',
      borderColor: 'rgba(75, 192, 192, 1)',
      borderWidth: 2,
      pointRadius: pointRadius.value,
      pointBackgroundColor: pointBackgroundColor.value,
      pointBorderColor: pointBackgroundColor.value,
      fill: true,
      data: mainDataValues.value,
      tension: 0.3,
    },
    ...neighborDatasets.value,
  ],
}))

const hasNeighbors = computed(() => neighborDatasets.value.length > 0)

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 150 },
  plugins: {
    legend: {
      display: hasNeighbors.value,
      labels: { font: { size: 11 }, boxWidth: 20 },
    },
    tooltip: {
      callbacks: {
        label: ctx => `${ctx.dataset.label}: ${ctx.raw?.toFixed(1)} V`,
      },
    },
    voltageThreshold: {
      thresholds: [
        { value: VOLTAGE_THRESHOLDS.WARNING, color: 'rgba(245, 158, 11, 0.9)', label: `${VOLTAGE_THRESHOLDS.WARNING} V` },
        { value: VOLTAGE_THRESHOLDS.CRITICAL, color: 'rgba(239, 68, 68, 0.95)', label: `${VOLTAGE_THRESHOLDS.CRITICAL} V` },
      ],
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: {
        font: { size: 10 },
        maxRotation: 45,
        autoSkip: true,
        maxTicksLimit: 24,
      },
    },
    y: {
      suggestedMin: 220,
      suggestedMax: yMaxSuggested.value,
      ticks: {
        font: { size: 10 },
        callback: val => `${val}V`,
      },
    },
  },
}))
</script>
